import { z } from "zod";
import { computedDecimalText, decimalCompare, decimalEqual, decimalSum, positiveDecimalSchema, type DecimalValue } from "../values/decimal";

import { expectedDeliveryDateIssues, type LifecycleDateLabels } from "./order-lifecycle-policy";
import { quantityLeft } from "./order-movement-policy";
import { ORDER_DELIVERY_SCHEDULE_COPY } from "./order-policy-copy";
import type { InsertOrder } from "../platform/schema";
import { toNoonUtcIso } from "../values/date-only";
import { recordIdSchema } from "../values/record-id";

export const MAX_DELIVERY_ENTRIES = 24;

export const DELIVERY_LINE_TEXT_MAX = 500;
const deliveryText = z.string().trim().max(DELIVERY_LINE_TEXT_MAX).transform((value) => value || undefined).optional();

export const deliveryEntrySchema = z.object({
  date: z.string().date().nullable(),
  quantity: positiveDecimalSchema,
  carrier: deliveryText,
  batchNumber: deliveryText,
  shipToAddressId: recordIdSchema.optional(),
});

export const DELIVERY_SCHEDULE_COPY = ORDER_DELIVERY_SCHEDULE_COPY;

export type DeliveryEntry = { date: string | null; quantity: DecimalValue; carrier?: string; batchNumber?: string; shipToAddressId?: number };

function normalizeDeliveryEntry(entry: DeliveryEntry): DeliveryEntry {
  return { ...entry, quantity: computedDecimalText(entry.quantity) };
}

export function normalizeDeliverySchedule(schedule: readonly DeliveryEntry[]): DeliveryEntry[] {
  return schedule.map(normalizeDeliveryEntry);
}

type LegacyDeliveryEntry = DeliveryEntry & { quantity: number };

function isLegacyDeliveryEntry(entry: DeliveryEntry): entry is LegacyDeliveryEntry {
  return typeof entry.quantity === "number";
}

function addLegacyQuantity(total: number, entry: LegacyDeliveryEntry): number {
  return total + entry.quantity;
}

export function normalizeLegacyOrderQuantity(quantity: DecimalValue, schedule: readonly DeliveryEntry[]): DecimalValue {
  if (schedule.length === 0 || !schedule.every(isLegacyDeliveryEntry)) return quantity;
  const legacyTotal = schedule.reduce(addLegacyQuantity, 0);
  if (!Number.isFinite(legacyTotal) || !decimalEqual(quantity, legacyTotal)) return quantity;
  return orderQuantityOf(schedule);
}

export const DELIVERY_SCHEDULE_FIELD = "deliverySchedule" satisfies keyof InsertOrder;
export const DELIVERY_ENTRY_FIELDS = deliveryEntrySchema.keyof().enum;

export function deliveryEntryPath(index: number, field: keyof DeliveryEntry): [string, number, string] {
  return [DELIVERY_SCHEDULE_FIELD, index, field];
}
export type DeliveryWithLeft = DeliveryEntry & { left: DecimalValue };

const UNDATED_LINE_SORTS_LAST = "~";

function sortKey(entry: DeliveryEntry): string {
  return entry.date ?? UNDATED_LINE_SORTS_LAST;
}

function byDate(left: DeliveryEntry, right: DeliveryEntry): number {
  const [a, b] = [sortKey(left), sortKey(right)];
  return a < b ? -1 : a > b ? 1 : 0;
}

export function sortDeliveries(schedule: readonly DeliveryEntry[]): DeliveryEntry[] {
  return [...schedule].sort(byDate);
}

function entryQuantity(entry: DeliveryEntry): DecimalValue {
  return entry.quantity;
}

export function orderQuantityOf(schedule: readonly DeliveryEntry[]): string {
  return decimalSum(schedule.map(entryQuantity));
}

export const deliveryScheduleSchema = z.array(deliveryEntrySchema).min(1).max(MAX_DELIVERY_ENTRIES).transform(sortDeliveries);

export function defaultDeliverySchedule(quantity: DecimalValue): DeliveryEntry[] {
  return [{ date: null, quantity: computedDecimalText(quantity) }];
}

type Allocation = { rows: DeliveryWithLeft[]; unallocated: DecimalValue };

function allocate({ rows, unallocated }: Allocation, entry: DeliveryEntry): Allocation {
  const left = quantityLeft(entry.quantity, unallocated);
  return { rows: [...rows, { ...entry, left }], unallocated: quantityLeft(unallocated, entry.quantity) };
}

export function deliveriesWithLeft(schedule: readonly DeliveryEntry[], movedQuantity: DecimalValue): DeliveryWithLeft[] {
  return sortDeliveries(schedule).reduce(allocate, { rows: [], unallocated: movedQuantity }).rows;
}

export function nextDueDelivery(schedule: readonly DeliveryEntry[], movedQuantity: DecimalValue): DeliveryWithLeft | null {
  return deliveriesWithLeft(schedule, movedQuantity).find((entry) => decimalCompare(entry.left, 0) > 0) ?? null;
}

export type AddressLabels = ReadonlyMap<number, string>;

export function deliveryDetails(entry: DeliveryEntry, labels?: AddressLabels): string[] {
  const copy = DELIVERY_SCHEDULE_COPY;
  const shipTo = entry.shipToAddressId == null ? null : `${copy.shipTo} ${labels?.get(entry.shipToAddressId) ?? `${copy.addressNumber}${entry.shipToAddressId}`}`;
  return [entry.carrier ? `${copy.via} ${entry.carrier}` : null, entry.batchNumber ? `${copy.batch} ${entry.batchNumber}` : null, shipTo]
    .filter((part): part is string => part != null);
}

function formatDeliveryEntry(labels: AddressLabels | undefined, entry: DeliveryEntry): string {
  const base = `${entry.date ?? DELIVERY_SCHEDULE_COPY.notScheduled}: ${entry.quantity}`;
  const details = deliveryDetails(entry, labels);
  return details.length === 0 ? base : `${base} (${details.join("; ")})`;
}

export function formatDeliverySchedule(schedule: readonly DeliveryEntry[], labels?: AddressLabels): string | null {
  if (schedule.length === 0) return null;
  return sortDeliveries(schedule).map(formatDeliveryEntry.bind(null, labels)).join("; ");
}

export function isOverdueDelivery(entry: DeliveryWithLeft, localToday: string): boolean {
  return decimalCompare(entry.left, 0) > 0 && entry.date !== null && entry.date < localToday;
}

type ScheduleIssue = { index: number; message: string };
type ScheduleLabels = LifecycleDateLabels & { expectedDeliveryDate?: string };

function entryIssues(submittedAt: Date, labels: ScheduleLabels | undefined, entry: DeliveryEntry, index: number): ScheduleIssue[] {
  if (!entry.date || !deliveryEntrySchema.safeParse(entry).success) return [];
  return expectedDeliveryDateIssues(submittedAt, new Date(toNoonUtcIso(entry.date)), labels).map(({ message }) => ({ index, message }));
}

export function deliveryScheduleIssues(
  submittedAt: Date | null | undefined,
  schedule: readonly DeliveryEntry[],
  labels?: ScheduleLabels,
): ScheduleIssue[] {
  if (!submittedAt) return [];
  return schedule.flatMap((entry, index) => entryIssues(submittedAt, labels, entry, index));
}

export function shipToAddressIds(schedule: readonly DeliveryEntry[] | undefined): number[] {
  return [...new Set((schedule ?? []).flatMap((entry) => entry.shipToAddressId ?? []))];
}
